import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { PUBLISH_ARTIFACT_TOOL, type Artifact, type Draft, type TranscriptEntry } from '@shared/protocol';
import { ArtifactCard } from './ArtifactCard';
import { Avatar } from './Avatar';
import { Markdown } from './Markdown';
import { ToolCard } from './ToolCard';
import { FILE_REF_PATTERN } from '../lib/files';

const time = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

/** A prompt's text with its @file references picked out. */
function PromptText({ text }: { text: string }) {
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(FILE_REF_PATTERN)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span className="file-ref" key={m.index}>
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

function Thinking({ entry }: { entry: TranscriptEntry }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="thinking">
      <button className="thinking__toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? '▾' : '▸'} Reasoning
      </button>
      {open && <div className="thinking__body">{entry.text}</div>}
    </div>
  );
}

interface EntryProps {
  entry: TranscriptEntry;
  artifacts: Artifact[];
  onOpenArtifact: (id: string, version: number) => void;
}

function Entry({ entry, artifacts, onOpenArtifact }: EntryProps) {
  switch (entry.kind) {
    case 'prompt':
      return (
        <div className="prompt">
          {entry.author && <Avatar user={entry.author} size="lg" />}
          <div className="prompt__body">
            <div className="prompt__meta">
              <span className="prompt__author">{entry.author?.name ?? 'Someone'}</span>
              <span className="prompt__time">{time.format(entry.at)}</span>
            </div>
            <div className="prompt__text">
              <PromptText text={entry.text ?? ''} />
            </div>
          </div>
        </div>
      );

    case 'text':
      return (
        <div className="say">
          <Markdown>{entry.text ?? ''}</Markdown>
          {entry.streaming && <span className="caret" aria-hidden />}
        </div>
      );

    case 'thinking':
      return <Thinking entry={entry} />;

    case 'tool':
      if (!entry.tool) return null;
      if (entry.tool.name === PUBLISH_ARTIFACT_TOOL) {
        return <ArtifactCard tool={entry.tool} artifacts={artifacts} onOpen={onOpenArtifact} />;
      }
      return <ToolCard tool={entry.tool} />;

    case 'result': {
      const r = entry.result;
      if (!r) return null;
      return (
        <div className={`turn-result${r.isError ? ' turn-result--error' : ''}`}>
          <span>
            {(r.durationMs / 1000).toFixed(1)}s · {r.numTurns} turn{r.numTurns === 1 ? '' : 's'} ·{' '}
            ${r.totalCostUsd.toFixed(4)}
          </span>
        </div>
      );
    }

    case 'notice':
      return (
        <div className={`notice notice--${entry.notice?.level ?? 'info'}`}>
          {entry.notice?.message}
        </div>
      );

    default:
      return null;
  }
}

/**
 * Someone else's message as they write it. Shaped like the prompt it will
 * become, so sending it reads as the draft settling rather than a new arrival.
 */
function DraftBubble({ draft, typing }: { draft: Draft; typing: boolean }) {
  return (
    <div className="prompt prompt--draft" aria-live="off">
      <Avatar user={draft.user} size="lg" />
      <div className="prompt__body">
        <div className="prompt__meta">
          <span className="prompt__author">{draft.user.name}</span>
          {/* Text left sitting in someone's box is a draft, not typing. */}
          <span className="prompt__time">{typing ? 'typing…' : 'draft'}</span>
        </div>
        <div className="prompt__text">
          <PromptText text={draft.text} />
          {typing && <span className="caret" aria-hidden />}
        </div>
      </div>
    </div>
  );
}

interface Props {
  entries: TranscriptEntry[];
  truncated: boolean;
  roomName: string;
  drafts: Draft[];
  /** User ids currently typing, from presence. */
  typingIds: Set<string>;
  artifacts: Artifact[];
  onOpenArtifact: (id: string, version: number) => void;
}

export function Transcript({
  entries,
  truncated,
  roomName,
  drafts,
  typingIds,
  artifacts,
  onOpenArtifact,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);

  // Track whether the reader is parked at the bottom. If they scrolled up to
  // read something, a streaming turn must not yank them back down.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  });

  // Jump to the bottom without animating when the room changes.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickRef.current = true;
    const previous = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    el.scrollTop = el.scrollHeight;
    el.style.scrollBehavior = previous;
  }, [roomName]);

  return (
    <div className="transcript" ref={scrollRef} onScroll={onScroll}>
      <div className="transcript__inner">
        {truncated && (
          <div className="transcript__truncated">
            Older messages were trimmed from the live buffer
          </div>
        )}

        {entries.length === 0 && drafts.length === 0 ? (
          <div className="transcript__empty">
            <h2>#{roomName} is quiet</h2>
            <p>
              Ask Claude something. Everyone in this room sees the same session, the same
              context, and the same answer — in real time.
            </p>
          </div>
        ) : (
          entries.map((entry) => (
            <Entry key={entry.id} entry={entry} artifacts={artifacts} onOpenArtifact={onOpenArtifact} />
          ))
        )}

        {drafts.map((d) => (
          <DraftBubble key={d.id} draft={d} typing={typingIds.has(d.user.id)} />
        ))}
      </div>
    </div>
  );
}
